import { apiGet, apiPost, apiUrl, getAuthHeader } from "@/services/studio/client";
import type { ArtifactValidation, RunEvent } from "@/services/studio/types";

export interface StreamHandlers {
  onEvent: (event: RunEvent) => void;
  onError?: (err: unknown) => void;
  onClose?: () => void;
}

export interface StreamHandle {
  abort: () => void;
}

interface JsonRunPayload {
  run_id?: string;
  status?: string;
  steps?: Array<Record<string, unknown>>;
  artifacts?: Array<string | Record<string, unknown>>;
  markdown?: string;
  final_output?: string;
}

function artifactValidationField(value: unknown): ArtifactValidation | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as ArtifactValidation)
    : null;
}

interface RunTicketPayload {
  ticket?: string;
  expires_in?: number;
  stream_path?: string;
}

export function streamRun(
  skillId: string,
  inputs: Record<string, unknown>,
  handlers: StreamHandlers
): StreamHandle {
  return startStreamingRun(
    `/api/playbooks/${encodeURIComponent(skillId)}/runs/stream`,
    `/api/playbooks/${encodeURIComponent(skillId)}/runs/ticket`,
    `/api/playbooks/${encodeURIComponent(skillId)}/runs`,
    { inputs, source: "manual" },
    handlers
  );
}

export function streamAgentRun(
  agentId: string,
  input: string,
  scope: Record<string, string> | undefined,
  handlers: StreamHandlers
): StreamHandle {
  return startStreamingRun(
    `/api/agents/${encodeURIComponent(agentId)}/runs/stream`,
    `/api/agents/${encodeURIComponent(agentId)}/runs/ticket`,
    `/api/agents/${encodeURIComponent(agentId)}/runs`,
    { input, scope, source: "manual" },
    handlers
  );
}

export function streamReplay(
  runId: string,
  handlers: StreamHandlers
): StreamHandle {
  return startJsonReplay(`/api/playbook-runs/${encodeURIComponent(runId)}`, handlers);
}

/**
 * Reconnect to an ALREADY-RUNNING agent run's live SSE replay stream (on browser
 * refresh). Unlike streamAgentRun (POST — starts a NEW run) this GETs the existing
 * run's stream so the store resumes live step/render/artifact/done events instead
 * of freezing on the one-shot JSON snapshot.
 */
export function reconnectAgentRun(
  runId: string,
  handlers: StreamHandlers
): StreamHandle {
  return connectRunStream(`/api/agent-runs/${encodeURIComponent(runId)}/stream`, handlers);
}

/**
 * Reconnect to an ALREADY-RUNNING playbook (migration) run's live SSE stream.
 * GET, not POST — resumes the existing run rather than starting a new one.
 */
export function reconnectPlaybookRun(
  runId: string,
  handlers: StreamHandlers
): StreamHandle {
  return connectRunStream(`/api/playbook-runs/${encodeURIComponent(runId)}/stream`, handlers);
}

/**
 * GET an existing run's live SSE stream and dispatch its frames through the same
 * event/data parser (readEventStream/emitSseFrame) that streamAgentRun/streamRun
 * use. No JSON fallback and no ticket handshake — this only ever connects to a
 * run that already exists, so there's no risk of creating a duplicate.
 */
function connectRunStream(streamPath: string, handlers: StreamHandlers): StreamHandle {
  const controller = new AbortController();

  void (async () => {
    try {
      const res = await fetch(apiUrl(streamPath), {
        method: "GET",
        credentials: "include",
        signal: controller.signal,
        headers: {
          Accept: "text/event-stream",
          ...getAuthHeader(),
        },
      });
      if (!res.ok || !res.body) {
        if (controller.signal.aborted) return;
        handlers.onEvent({
          event: "error",
          message: `reconnect stream HTTP ${res.status}`,
        });
        handlers.onError?.(new Error(`reconnect stream HTTP ${res.status}`));
        handlers.onClose?.();
        return;
      }
      await readEventStream(res, handlers, controller);
      handlers.onClose?.();
    } catch (err) {
      if (controller.signal.aborted) return;
      handlers.onEvent({
        event: "error",
        message: err instanceof Error ? err.message : String(err),
      });
      handlers.onError?.(err);
      handlers.onClose?.();
    }
  })();

  return {
    abort: () => controller.abort(),
  };
}

function startStreamingRun(
  streamPath: string,
  ticketPath: string,
  fallbackPath: string,
  body: unknown,
  handlers: StreamHandlers
): StreamHandle {
  const controller = new AbortController();
  // Track whether the SSE stream has emitted at least one event, meaning a run
  // was already started on the server. Once true, the JSON fallback must NOT
  // fire — doing so would create a second, duplicate run.
  let streamStarted = false;

  const guardedHandlers: StreamHandlers = {
    ...handlers,
    onEvent(event) {
      streamStarted = true;
      handlers.onEvent(event);
    },
  };

  void (async () => {
    try {
      const ticketPayload = await apiPost<RunTicketPayload>(ticketPath, {});
      const ticket = ticketPayload.ticket;
      if (!ticket) {
        await runJsonFallback(fallbackPath, body, guardedHandlers, controller);
        return;
      }
      const streamBody = withRunTicket(body, ticket);
      const res = await fetch(apiUrl(streamPath), {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...getAuthHeader(),
        },
        body: JSON.stringify(streamBody),
      });
      if (!res.ok || !res.body) {
        if (!streamStarted) {
          await runJsonFallback(fallbackPath, body, guardedHandlers, controller);
        }
        return;
      }
      await readEventStream(res, guardedHandlers, controller);
      handlers.onClose?.();
    } catch (err) {
      if (controller.signal.aborted) return;
      // If the stream already started a run on the server, do not fall back —
      // the fallback would create a duplicate run. Surface the error instead.
      if (streamStarted) {
        handlers.onEvent({
          event: "error",
          message: err instanceof Error ? err.message : String(err),
        });
        handlers.onError?.(err);
        handlers.onClose?.();
        return;
      }
      await runJsonFallback(fallbackPath, body, guardedHandlers, controller).catch((fallbackErr) => {
        if (controller.signal.aborted) return;
        handlers.onEvent({
          event: "error",
          message: fallbackErr instanceof Error ? fallbackErr.message : String(fallbackErr),
        });
        handlers.onError?.(fallbackErr);
        handlers.onClose?.();
      });
    }
  })();

  return {
    abort: () => controller.abort(),
  };
}

function withRunTicket(body: unknown, ticket: string): Record<string, unknown> {
  if (body && typeof body === "object" && !Array.isArray(body)) {
    return { ...(body as Record<string, unknown>), ticket };
  }
  return { ticket };
}

async function runJsonFallback(
  path: string,
  body: unknown,
  handlers: StreamHandlers,
  controller: AbortController
) {
  try {
    handlers.onEvent({ event: "started" });
    const payload = await apiPost<JsonRunPayload>(path, body, {
      signal: controller.signal,
    });
    if (controller.signal.aborted) return;
    emitJsonRunPayload(payload, handlers);
    handlers.onClose?.();
  } catch (err) {
    if (controller.signal.aborted) return;
    handlers.onEvent({
      event: "error",
      message: err instanceof Error ? err.message : String(err),
    });
    handlers.onError?.(err);
    handlers.onClose?.();
  }
}

async function readEventStream(
  res: Response,
  handlers: StreamHandlers,
  controller: AbortController
) {
  const reader = res.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = "";
  while (!controller.signal.aborted) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      emitSseFrame(frame, handlers);
      boundary = buffer.indexOf("\n\n");
    }
  }
  if (buffer.trim()) emitSseFrame(buffer, handlers);
}

function emitSseFrame(frame: string, handlers: StreamHandlers) {
  let eventName = "message";
  const dataLines: string[] = [];
  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trimStart());
    }
  }
  if (!dataLines.length) return;
  try {
    const payload = JSON.parse(dataLines.join("\n")) as RunEvent;
    handlers.onEvent({ ...payload, event: payload.event ?? (eventName as RunEvent["event"]) });
  } catch {
    handlers.onEvent({ event: eventName as RunEvent["event"], message: dataLines.join("\n") });
  }
}

function startJsonReplay(path: string, handlers: StreamHandlers): StreamHandle {
  const controller = new AbortController();

  void (async () => {
    try {
      const payload = await apiGet<Record<string, unknown>>(path, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const run = asRecord(payload.run);
      const steps = Array.isArray(payload.steps) ? payload.steps as Array<Record<string, unknown>> : [];
      const artifacts = Array.isArray(payload.artifacts) ? payload.artifacts as Array<Record<string, unknown>> : [];
      emitJsonRunPayload(
        {
          run_id: stringField(run?.id),
          status: stringField(run?.status) || "done",
          markdown: stringField(run?.result_md),
          steps,
          artifacts,
        },
        handlers
      );
      handlers.onClose?.();
    } catch (err) {
      if (controller.signal.aborted) return;
      handlers.onEvent({
        event: "error",
        message: err instanceof Error ? err.message : String(err),
      });
      handlers.onError?.(err);
      handlers.onClose?.();
    }
  })();

  return {
    abort: () => controller.abort(),
  };
}

function emitJsonRunPayload(payload: JsonRunPayload, handlers: StreamHandlers) {
  if (payload.run_id) {
    handlers.onEvent({ event: "started", run_id: payload.run_id });
  }

  for (const step of payload.steps ?? []) {
    const stepId = stringField(step.step_id) || stringField(step.step) || stringField(step.id);
    const tool = stringField(step.tool);
    handlers.onEvent({
      event: "step_start",
      step_id: stepId,
      tool,
    });
    handlers.onEvent({
      event: "step_result",
      step_id: stepId,
      tool,
      ok: step.ok !== false && stringField(step.status) !== "failed",
      data: step.data ?? step.result,
      message: stringField(step.error),
      attempts: typeof step.attempts === "number" ? step.attempts : undefined,
    });
  }

  const markdown = payload.markdown || payload.final_output;
  if (markdown) {
    handlers.onEvent({ event: "render", markdown });
  }

  for (const artifact of payload.artifacts ?? []) {
    if (typeof artifact === "string") {
      handlers.onEvent({
        event: "artifact",
        name: artifact,
        content_type: contentTypeForName(artifact),
      });
      continue;
    }
    const name = stringField(artifact.name) || stringField(artifact.filename);
    if (!name) continue;
    handlers.onEvent({
      event: "artifact",
      name,
      content_type: stringField(artifact.content_type) || contentTypeForName(name),
      size_bytes: typeof artifact.size_bytes === "number" ? artifact.size_bytes : 0,
      // The check-only validation verdict rides along on the artifact row. Dropping it
      // here would leave a replayed run showing no verdict at all, so a file that was
      // never validated would look identical to one that passed.
      validation: artifactValidationField(artifact.validation),
    });
  }

  // Run status, NOT validation status — the two vocabularies are separate. A check-only
  // validation never sets the run status, so `validated`/`unvalidated`/`validation_failed`
  // can never reach this allowlist.
  if (payload.status && !["done", "partial", "input_required", "action_blocked"].includes(payload.status)) {
    handlers.onEvent({ event: "error", message: payload.status });
  } else {
    handlers.onEvent({ event: "done", status: payload.status ?? "done" });
  }
}

function stringField(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function contentTypeForName(name: string): string {
  if (name.endsWith(".md")) return "text/markdown";
  if (name.endsWith(".html")) return "text/html";
  if (name.endsWith(".json")) return "application/json";
  if (name.endsWith(".csv")) return "text/csv";
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".xml")) return "application/xml";
  if (name.endsWith(".cls") || name.endsWith(".trigger") || name.endsWith(".soql")) return "text/plain";
  return "application/octet-stream";
}
